import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from './useAuth';
import { FITTER_ONLY_BLOCKED_EVENT } from './apiClient';
import { fitterRedirectFor, isFitterOnlyGated } from '@/features/fitter/fitterAccess';

/**
 * Story `fe-be-monteur-s1-basis` (EPIC-021, S021.1) — houdt een alleen-monteur in
 * de monteur-weergave. Eén layout-route, geen per-plek-oplossingen (precedent C1 van
 * EPIC-019: bel-menu, logo en bookmarks kiezen ook routes).
 *
 * <b>Hij staat DIRECT boven `<Chrome>`</b> (tussen `<ProtectedRoute>` en `<Chrome>`
 * in `routes.tsx`), dus nog vóór `InsurerRouteGuard` en `RestrictedRepairerRouteGuard`.
 * Stond hij eronder, dan mountte `<Chrome>` eerst en vuurde die zijn eigen calls af —
 * voor een alleen-monteur allemaal 403.
 *
 * - `fitterOnly === true` of `resolved === false` → `<Navigate to="/monteur" replace>`.
 * - Zolang whoami laadt (ook na `queryClient.clear()` bij switch-user) rendert hij
 *   NIETS, zodat `<Chrome>` niet even mount (TA-review F-15).
 * - Een 403 `fitter_only_not_allowed` op welke call dan ook (het schuifje werd aangezet
 *   terwijl de gebruiker in de kantoor-app zat) laat whoami opnieuw ophalen; daarna
 *   stuurt deze guard door (TA-review F-16). Bewust geen tweede `useQuery` op
 *   `['auth','whoami']`: invalideren laat de bestaande query in `AuthProvider` refetchen.
 */
export function FitterOnlyRouteGuard() {
    const { user, isLoading } = useAuth();
    const queryClient = useQueryClient();
    const location = useLocation();

    useEffect(() => {
        const onBlocked = () => {
            void queryClient.invalidateQueries({ queryKey: ['auth', 'whoami'] });
        };
        window.addEventListener(FITTER_ONLY_BLOCKED_EVENT, onBlocked);
        return () => window.removeEventListener(FITTER_ONLY_BLOCKED_EVENT, onBlocked);
    }, [queryClient]);

    if (isLoading || !user) return null;

    if (isFitterOnlyGated(user)) {
        // S3 (AC 36): de supportlink uit de mail of het belletje komt op het monteur-scherm van die melding.
        return <Navigate to={fitterRedirectFor(location.pathname)} replace />;
    }

    return <Outlet />;
}
